import {
  GridRenderCellParams,
  DataGrid,
  GridAlignment,
  GridColDef,
} from '@mui/x-data-grid';
import React from 'react';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';
import {GridDurationColumn} from '../../../components/grid/GridColumns';
import {MediaTrackSelection} from './MusicSelectionForm';
import {DataGridCustPagination} from '../../../components/utlis/DataGrid/Pagination';
import {IMediaTrack} from '../../../dto/MediaTrack.dto';
import {
  Box,
  Chip,
  Skeleton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import SecondsNumberToTimeFormat from '../../../components/utlis/date-time/SecondsNumberToTimeFormat';
import {AccessTime} from '@mui/icons-material';
import { closePlayer } from '../../../store/Slices/playerSlice';
import { useDispatch } from 'react-redux';

interface MusicSelectionDataGridProps {
  data: IMediaTrack[];
  isFetching: boolean;
  total: number;
  pagination: {
    pageSize: number;
    page: number;
  };
  setPagination: React.Dispatch<
    React.SetStateAction<{pageSize: number; page: number}>
  >;
  selectedSongIds: number[];
  setSelectedSongs: React.Dispatch<React.SetStateAction<IMediaTrack[]>>;
  setSelectedSongIds: React.Dispatch<React.SetStateAction<number[]>>;
}

const MusicSelectionDataGrid = ({
  data,
  isFetching,
  total,
  pagination,
  setPagination,
  selectedSongIds,
  setSelectedSongIds,
  setSelectedSongs,
}: MusicSelectionDataGridProps) => {
  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'url',
        type: 'actions',
        headerName: 'Play',
        width: 54,
        align: 'center' as GridAlignment,
        renderCell: (params: GridRenderCellParams) =>
          params.row.url ? (
            <MusicPreviewBtn
              id={params.row.id}
              url={params.row.url}
              track_name={params.row.track_name}
              track_artist={params.row.track_artist}
            />
          ) : null,
      },
      {
        field: 'track_artist',
        headerName: 'Artist Name',
        flex: 1,
        align: 'left' as GridAlignment,
      },
      {
        field: 'track_name',
        headerName: 'Song title',
        hideable: false,
        flex: 1,
        align: 'left' as GridAlignment,
      },
      {
        ...GridDurationColumn,
        align: 'center' as GridAlignment,
      },
      {
        field: 'edit',
        headerName: 'Manage',
        hideable: false,
        align: 'center' as GridAlignment,
        headerAlign: 'center' as GridAlignment,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <MediaTrackSelection
              selectedSongIds={selectedSongIds}
              setSelectedSongIds={setSelectedSongIds}
              setSelectedSongs={setSelectedSongs}
              track={params.row}
            />
          );
        },
      },
    ],
    [selectedSongIds, setSelectedSongIds, setSelectedSongs],
  );
  return (
    <DataGrid
      sx={{minHeight: 500, maxHeight: 500}}
      rows={data ?? []}
      columns={columns}
      loading={isFetching}
      rowCount={total ?? 0}
      pageSizeOptions={[10, 25, 50]}
      paginationModel={pagination}
      paginationMode='server'
      onPaginationModelChange={setPagination}
      disableRowSelectionOnClick
      disableColumnMenu
      slots={{
        pagination: DataGridCustPagination,
      }}
      slotProps={{
        columnsPanel: {
          disableHideAllButton: true,
          disableShowAllButton: true,
        },
      }}
    />
  );
};

export default MusicSelectionDataGrid;

export const MusicSelectionTiles = ({
  data,
  isFetching,
  total,
  pagination,
  setPagination,
  selectedSongIds,
  setSelectedSongIds,
  setSelectedSongs,
}: MusicSelectionDataGridProps) => {
  const dispatch = useDispatch();
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    dispatch(closePlayer());
    setPagination(prev => ({...prev, page: newPage}));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    dispatch(closePlayer());
    setPagination(prev => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  if (isFetching) {
    return (
      <Box className='h-full w-full'>
        <Stack spacing={1} p={1}>
          {Array.from({length: 5}).map((_, index) => (
            <Skeleton
              key={index}
              variant='rectangular'
              height={72}
              sx={{borderRadius: 1}}
            />
          ))}
        </Stack>
      </Box>
    );
  }
  return (
    <Box className='h-full w-full'>
      <Box className='calcheight-52 scrollbar overflow-auto w-full'>
        {data.map(track => (
          <StudentSingleTrackTile
            key={track.id}
            track={track}
            selectedSongIds={selectedSongIds}
            setSelectedSongIds={setSelectedSongIds}
            setSelectedSongs={setSelectedSongs}
          />
        ))}
      </Box>
      <TablePagination
        component='div'
        count={total}
        page={pagination.page}
        onPageChange={handleChangePage}
        rowsPerPage={pagination.pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage=''
      />
    </Box>
  );
};

export const StudentSingleTrackTile = React.memo(
  ({
    isChipVisible = true,
    isDeleteIcon = false,
    track,
    selectedSongIds,
    setSelectedSongs,
    setSelectedSongIds,
  }: {
    isChipVisible?: boolean;
    isDeleteIcon?: boolean;
    track: IMediaTrack;
    selectedSongIds: number[];
    setSelectedSongs: React.Dispatch<React.SetStateAction<IMediaTrack[]>>;
    setSelectedSongIds: React.Dispatch<React.SetStateAction<number[]>>;
  }) => {
    return (
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        className='radius-5'
        sx={{border: '1px solid var(--blue-variant2)'}}
        spacing={1}
        m={1}
        p={1}>
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          className='w-full'>
          {track.url ? (
            <MusicPreviewBtn
              id={track.id}
              url={track.url}
              track_name={track.track_name}
              track_artist={track.track_artist}
            />
          ) : null}
          <Box className='w-full'>
            <Typography
              sx={{fontSize: '0.875rem'}}
              align='left'
              className='font-bold'>
              {track.track_name}
            </Typography>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              justifyContent='space-between'>
              <Typography
                sx={{fontSize: '0.875rem'}}
                align='left'
                className='text-gray-variant7'>
                {track.track_artist}
              </Typography>
              {isChipVisible && (
                <Chip
                  size='small'
                  icon={<AccessTime />}
                  label={SecondsNumberToTimeFormat(track.duration)}
                />
              )}
            </Stack>
          </Box>
        </Stack>
        <MediaTrackSelection
          selectedSongIds={selectedSongIds}
          setSelectedSongIds={setSelectedSongIds}
          setSelectedSongs={setSelectedSongs}
          track={track}
          isDeleteIcon={isDeleteIcon}
        />
      </Stack>
    );
  },
);
